import './style/productDetailPanel.scss';
import {AwesomeIcon, enterPressed, nullify, ResponseData, showSnackbar, stopPropagation} from '@dvrd/dvr-controls';

import {Icon} from '@iconify/react';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import {BolmateButton, BolmateCollapsibleSection, BolmatePlot} from '../../controls';
import {SEARCH_VOLUMES_ROUTE} from '../../../util/constants';
import {setNavigationParam} from '../../../util/navigateUtil';
import {
    archiveSearchRankingCategory,
    archiveSearchRankingKeyword,
    getSearchRankingBuyboxTrend,
    getSearchRankingCategoryTrend,
    getSearchRankingKeywordsTrend,
    getSearchRankingProductCategories,
    getSearchRankingProductKeywords,
    getSuggestedKeywords
} from '../../../util/requestSender';
import RankCell from './rankCell';
import BolmateTooltip from '../../controls/tooltip/bolmateTooltip';
import classNames from 'classnames';
import {
    BolmatePlotSeries,
    OrderDirection,
    SearchRankingsBuyboxTrendPoint,
    SearchRankingsCategoryData,
    SearchRankingsKeywordData,
    SearchRankingsKeywordTrends,
    SearchRankingsTrendPoint,
    TrackedKeyword
} from '../../../util/interfaces';
import {SearchRankingMode, SearchRankingsSortKey, SuggestedKeyword} from '../../../util/types';
import {formatCurrency} from '../../../util/utils';

interface Props {
    accountId: string;
    ean: string;
    countryCodes: Array<'NL' | 'BE'>;
    onClose: VoidFunction;
    onAddKeyword: (
        keyword: string,
        ean: string,
        callback: (keyword: TrackedKeyword, restored: boolean) => void
    ) => void;
    onKeywordsChanged: VoidFunction;
    rankingMode: SearchRankingMode;
    manualKeywordCount: number;
    manualKeywordLimit: number | null;
}

function getSortValue(kw: SearchRankingsKeywordData, key: SearchRankingsSortKey): string | number {
    switch (key) {
        case 'label':
            return kw.keyword.toLowerCase();
        case 'volume':
            return kw.search_volume ?? -1;
        case 'winning_bid':
            return kw.winning_bid ?? -1;
        case 'week':
            return kw.rank_week ?? 9999;
        case 'month':
            return kw.rank_month ?? 9999;
        default:
            return 9999;
    }
}

function getCatSortValue(cat: SearchRankingsCategoryData, key: SearchRankingsSortKey): string | number {
    switch (key) {
        case 'label':
            return cat.category_name.toLowerCase();
        case 'volume':
            return cat.search_volume ?? -1;
        case 'winning_bid':
            return cat.winning_bid ?? -1;
        case 'week':
            return cat.rank_week ?? 9999;
        case 'month':
            return cat.rank_month ?? 9999;
        default:
            return -1;
    }
}

const MAX_KEYWORDS = 30;

export default function ProductDetailPanel(props: Props) {
    const {
        accountId,
        ean,
        countryCodes,
        onClose,
        onAddKeyword,
        rankingMode,
        onKeywordsChanged,
        manualKeywordCount,
        manualKeywordLimit
    } = props;
    const navigate = useNavigate();

    // Keywords state
    const [keywords, setKeywords] = useState<Array<SearchRankingsKeywordData>>([]);
    const [suggestedKeywords, setSuggestedKeywords] = useState<Array<SuggestedKeyword>>([]);
    const [loadingKeywords, setLoadingKeywords] = useState(true);
    const [loadingSuggestedKeywords, setLoadingSuggestedKeywords] = useState(true);
    const [showArchivedKeywords, setShowArchivedKeywords] = useState(false);
    const [showArchivedCategories, setShowArchivedCategories] = useState(false);
    const [kwSortKey, setKwSortKey] = useState<SearchRankingsSortKey>('week');
    const [kwSortOrder, setKwSortOrder] = useState<OrderDirection>(OrderDirection.ASC);

    // Categories state
    const [categories, setCategories] = useState<Array<SearchRankingsCategoryData>>([]);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [catSortKey, setCatSortKey] = useState<SearchRankingsSortKey>('week');
    const [catSortOrder, setCatSortOrder] = useState<OrderDirection>(OrderDirection.ASC);

    // Keyword trend state
    const [kwTrendItems, setKwTrendItems] = useState<Array<BolmatePlotSeries>>([]);
    const [kwTrendSelectedIds, setKwTrendSelectedIds] = useState<Array<string>>([]);
    const [kwTrendLoading, setKwTrendLoading] = useState(false);

    // Category trend state
    const [catTrendItems, setCatTrendItems] = useState<Array<BolmatePlotSeries>>([]);
    const [catTrendSelectedIds, setCatTrendSelectedIds] = useState<Array<string>>([]);
    const [catTrendLoading, setCatTrendLoading] = useState(false);

    // Buybox trend state
    const [buyboxTrendData, setBuyboxTrendData] = useState<Array<BolmatePlotSeries>>([]);
    const [buyboxLoading, setBuyboxLoading] = useState(true);

    // Add keyword value
    const [addKeywordValue, setAddKeywordValue] = useState('');

    const keywordsTrendsAbort = useRef<AbortController | null>(null);
    const categoriesTrendsAbort = useRef<AbortController | null>(null);

    const keywordsLimitReached = useMemo(() => {
        if (manualKeywordLimit === null) return false;
        return manualKeywordCount >= manualKeywordLimit;
    }, [manualKeywordCount, manualKeywordLimit]);

    // Load keywords
    function loadSuggestedKeywords(_keywords?: Array<SearchRankingsKeywordData>) {
        if (keywordsLimitReached) return setLoadingSuggestedKeywords(false);
        setLoadingSuggestedKeywords(true);
        if (!_keywords) _keywords = keywords;
        if (_keywords.length >= MAX_KEYWORDS) return setLoadingSuggestedKeywords(false);
        const exclude = nullify(
            suggestedKeywords.map(keyword => keyword.keyword).concat(_keywords.map(keyword => keyword.keyword))
        );
        getSuggestedKeywords({
            data: {
                ean,
                account_id: accountId,
                exclude_keywords: exclude
            },
            callback: (data: ResponseData) => {
                setLoadingSuggestedKeywords(false);
                if (data.success) {
                    setSuggestedKeywords((current: Array<SuggestedKeyword>) => {
                        let newSuggested = current.concat(
                            data.keywords.map((keyword: string) => ({
                                keyword
                            }))
                        );
                        // Limit total keywords to MAX_KEYWORDS by removing the "oldest" suggested keywords
                        if (newSuggested.length + _keywords.length > MAX_KEYWORDS)
                            newSuggested = newSuggested.slice(_keywords.length - 1);
                        return newSuggested;
                    });
                }
            }
        });
    }

    function loadMoreSuggestedKeywords() {
        setSuggestedKeywords(suggestedKeywords.map((keyword: SuggestedKeyword) => ({...keyword, accepted: false})));
        loadSuggestedKeywords();
    }

    const loadKeywords = useCallback(() => {
        setLoadingKeywords(true);
        getSearchRankingProductKeywords({
            data: {
                account_id: accountId,
                ean,
                country_codes: countryCodes,
                include_archived: true,
                ranking_mode: rankingMode
            },
            callback: (data: ResponseData) => {
                setLoadingKeywords(false);
                if (data.success) {
                    const keywords = data.keywords ?? [];
                    setKeywords(keywords);
                    loadSuggestedKeywords(keywords);
                } else showSnackbar('Fout bij het laden van keywords');
            }
        });
    }, [accountId, ean, countryCodes, rankingMode]);

    function onChangeKeywordValue(evt: React.ChangeEvent<HTMLInputElement>) {
        const value = evt.currentTarget.value;
        setAddKeywordValue(value);
    }

    function onKeywordValueKeyDown(evt: React.KeyboardEvent) {
        if (enterPressed(evt)) onSubmitKeywordValue();
    }

    function onSubmitKeywordValue() {
        const keyword = addKeywordValue.trim();
        if (!keyword.length) return;
        onAddKeyword(keyword, ean, onKeywordAdded);
        setAddKeywordValue('');
    }

    function onAcceptKeyword(keyword: string) {
        return function () {
            onAddKeyword(keyword, ean, onKeywordAdded);
        };
    }

    function onKeywordAdded(tracked: TrackedKeyword, restored: boolean) {
        setSuggestedKeywords(
            suggestedKeywords.filter((keyword: SuggestedKeyword) => keyword.keyword !== tracked.keyword)
        );
        if (restored) {
            setKeywords(
                keywords.map((keyword: SearchRankingsKeywordData) => {
                    if (keyword.keyword === tracked.keyword) return {...keyword, is_archived: false};
                    return keyword;
                })
            );
        } else {
            const newKeyword: SearchRankingsKeywordData = {
                keyword: tracked.keyword,
                is_archived: false,
                rank_last_month: null,
                rank_last_week: null,
                rank_month: null,
                rank_week: null,
                source: 'MANUAL',
                search_volume: null,
                winning_bid: null
            };
            setKeywords(keywords.concat(newKeyword));
        }
    }

    function onDenyKeyword(keyword: string) {
        return function () {
            setSuggestedKeywords(
                suggestedKeywords.map((suggested: SuggestedKeyword) => {
                    if (suggested.keyword === keyword) return {...suggested, accepted: false};
                    return suggested;
                })
            );
        };
    }

    // Load categories
    const loadCategories = useCallback(() => {
        setLoadingCategories(true);
        getSearchRankingProductCategories({
            data: {
                account_id: accountId,
                ean,
                country_codes: countryCodes,
                ranking_mode: rankingMode
            },
            callback: (data: ResponseData) => {
                setLoadingCategories(false);
                if (data.success) {
                    setCategories(data.categories || []);
                } else {
                    setCategories([]);
                    showSnackbar('Fout bij het laden van categorie\u00EBn');
                }
            }
        });
    }, [accountId, ean, countryCodes, rankingMode]);

    // Load buybox trend
    const loadBuyboxTrend = useCallback(() => {
        setBuyboxLoading(true);
        getSearchRankingBuyboxTrend({
            data: {
                account_id: accountId,
                ean,
                country_codes: countryCodes
            },
            callback: (data: ResponseData) => {
                setBuyboxLoading(false);
                if (data.success && data.trend_data && data.trend_data.length > 0) {
                    setBuyboxTrendData([
                        {
                            id: 'buybox',
                            name: 'Koopblok %',
                            data: data.trend_data.map((p: SearchRankingsBuyboxTrendPoint) => ({
                                date: p.date,
                                value: p.buybox_percentage
                            }))
                        }
                    ]);
                } else {
                    setBuyboxTrendData([]);
                }
            }
        });
    }, [accountId, ean, countryCodes]);

    useEffect(() => {
        loadKeywords();
        loadCategories();
        loadBuyboxTrend();
    }, [loadKeywords, loadCategories, loadBuyboxTrend]);

    function loadKeywordsTrends(keywords: Array<string>, showLoading: boolean = true) {
        keywordsTrendsAbort.current?.abort();
        if (keywords.length === 0) {
            setKwTrendItems([]);
            return;
        }
        if (showLoading) setKwTrendLoading(true);
        const results: Array<BolmatePlotSeries> = [];
        keywordsTrendsAbort.current = getSearchRankingKeywordsTrend({
            data: {
                account_id: accountId,
                ean,
                keywords,
                country_codes: countryCodes
            },
            callback: (data: ResponseData) => {
                keywordsTrendsAbort.current = null;
                if (data.success) {
                    const responseData: SearchRankingsKeywordTrends = data.trend_data;
                    for (const [keyword, trendData] of Object.entries(responseData)) {
                        results.push({
                            id: keyword,
                            name: keyword,
                            data: trendData.map((trend: SearchRankingsTrendPoint) => ({
                                date: trend.date,
                                value: trend.rank
                            }))
                        });
                    }
                }
                setKwTrendItems(results);
                setKwTrendLoading(false);
            }
        });
    }

    // Load category trends when selectedIds change
    const loadCategoryTrends = useCallback(
        (categoryIDs: Array<string>, showLoading = true) => {
            categoriesTrendsAbort.current?.abort();
            if (categoryIDs.length === 0) {
                setCatTrendItems([]);
                return;
            }
            if (showLoading) setCatTrendLoading(true);
            categoriesTrendsAbort.current = getSearchRankingCategoryTrend({
                data: {
                    account_id: accountId,
                    ean,
                    country_codes: countryCodes
                },
                callback: (data: ResponseData) => {
                    categoriesTrendsAbort.current = null;
                    if (data.success) {
                        const trendData: Record<
                            string,
                            {
                                category_name: string;
                                values: Array<{date: string; value: number | null}>;
                            }
                        > = data.trend_data || {};
                        const results: Array<BolmatePlotSeries> = Object.entries(trendData).map(entry => ({
                            id: entry[0],
                            name: entry[1].category_name,
                            data: entry[1].values
                        }));
                        setCatTrendItems(results);
                    }
                    setCatTrendLoading(false);
                }
            });
        },
        [accountId, ean, countryCodes]
    );

    // Track which base keywords are currently fetched
    const fetchedKeywordsRef = useRef<Set<string>>(new Set());

    // Auto-select all keywords for trend chart once keywords load
    useEffect(() => {
        if (keywords.length > 0 && kwTrendSelectedIds.length === 0) {
            const activeKeywords = keywords.filter(k => !k.is_archived);
            const allKeywords = activeKeywords.map(k => k.keyword);
            setKwTrendSelectedIds(allKeywords);
            fetchedKeywordsRef.current = new Set(allKeywords);
            loadKeywordsTrends(allKeywords);
        }
    }, [keywords]);

    // Auto-select sponsored trend items only on first appearance
    const knownSponsoredIds = useRef<Set<string>>(new Set());
    useEffect(() => {
        const sponsoredIds = kwTrendItems.filter(i => i.dashed).map(i => i.id);
        const brandNew = sponsoredIds.filter(id => !knownSponsoredIds.current.has(id));
        if (brandNew.length > 0) {
            for (const id of brandNew) knownSponsoredIds.current.add(id);
            setKwTrendSelectedIds(prev => [...prev, ...brandNew.filter(id => !prev.includes(id))]);
        }
    }, [kwTrendItems]);

    function handleKwTrendSelectionChange(ids: Array<string>) {
        setKwTrendSelectedIds(ids);
        // Extract unique base keywords (strip __sponsored suffix)
        const baseKeywords = [...new Set(ids.map(id => id.replace(/__sponsored$/, '')))];
        // Only refetch if new keywords were added that we don't have data for
        const currentFetched = fetchedKeywordsRef.current;
        const newKeywords = baseKeywords.filter(kw => !currentFetched.has(kw));
        if (newKeywords.length > 0) {
            for (const kw of newKeywords) fetchedKeywordsRef.current.add(kw);
            loadKeywordsTrends(baseKeywords, false);
        }
    }

    // Track which categories are currently fetched
    const fetchedCategoriesRef = useRef<Set<string>>(new Set());

    // Auto-select all categories for trend chart once categories load
    useEffect(() => {
        if (categories.length > 0 && catTrendSelectedIds.length === 0) {
            const categoryIDs = categories.map(c => c.category_id);
            setCatTrendSelectedIds(categoryIDs);
            fetchedCategoriesRef.current = new Set(categoryIDs);
            loadCategoryTrends(categoryIDs);
        }
    }, [categories]);

    const handleCatTrendSelectionChange = useCallback(
        (categoryIDs: Array<string>) => {
            setCatTrendSelectedIds(categoryIDs);
            // Only refetch if new categories were added that we don't have data for
            const currentFetched = fetchedCategoriesRef.current;
            const newCategories = categoryIDs.filter(id => !currentFetched.has(id));
            if (newCategories.length > 0) {
                for (const id of newCategories) fetchedCategoriesRef.current.add(id);
                loadCategoryTrends(categoryIDs, false);
            }
        },
        [loadCategoryTrends]
    );

    // Archive/delete handlers
    function onArchiveKeyword(keyword: string, isArchived: boolean) {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            // Optimistically toggle the archived flag so the UI updates instantly
            setKeywords(prev => prev.map(kw => (kw.keyword === keyword ? {...kw, is_archived: !isArchived} : kw)));
            archiveSearchRankingKeyword({
                data: {
                    account_id: accountId,
                    ean,
                    keyword,
                    is_archived: !isArchived
                },
                callback: (data: ResponseData) => {
                    if (data.success) {
                        showSnackbar(isArchived ? 'Keyword hersteld' : 'Keyword gearchiveerd');
                        onKeywordsChanged();
                    } else {
                        // Revert on failure
                        setKeywords(prev =>
                            prev.map(kw => (kw.keyword === keyword ? {...kw, is_archived: isArchived} : kw))
                        );
                        showSnackbar('Fout bij het archiveren van keyword');
                    }
                }
            });
        };
    }

    function onArchiveCategory(categoryID: string, archive: boolean) {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            // Optimistically toggle the archived flag so the UI updates instantly
            setCategories((categories: Array<SearchRankingsCategoryData>) =>
                categories.map(category =>
                    category.category_id === categoryID ? {...category, is_archived: archive} : category
                )
            );
            archiveSearchRankingCategory({
                data: {
                    account_id: accountId,
                    category_id: categoryID,
                    archive
                },
                callback: (data: ResponseData) => {
                    if (data.success) {
                        showSnackbar(archive ? 'Categorie gearchiveerd' : 'Categorie hersteld');
                    } else {
                        // Revert on failure
                        setCategories((categories: Array<SearchRankingsCategoryData>) =>
                            categories.map(category =>
                                category.category_id === categoryID ? {...category, is_archived: !archive} : category
                            )
                        );
                        showSnackbar('Fout bij het archiveren de categorie');
                    }
                }
            });
        };
    }

    // Sorted & filtered keywords
    const displayKeywords = useMemo(() => {
        const filtered = showArchivedKeywords ? keywords : keywords.filter(k => !k.is_archived);
        return [...filtered].sort((a, b) => {
            const aVal = getSortValue(a, kwSortKey);
            const bVal = getSortValue(b, kwSortKey);
            if (aVal < bVal) return kwSortOrder === OrderDirection.ASC ? -1 : 1;
            if (aVal > bVal) return kwSortOrder === OrderDirection.ASC ? 1 : -1;
            return 0;
        });
    }, [keywords, showArchivedKeywords, kwSortKey, kwSortOrder]);

    const displaySuggestedKeywords = useMemo(
        () => suggestedKeywords.filter((suggested: SuggestedKeyword) => suggested.accepted !== false),
        [suggestedKeywords]
    );

    const archivedKeywordsCount = useMemo(() => keywords.filter(k => k.is_archived).length, [keywords]);
    const archivedCategoriesCount = useMemo(() => categories.filter(c => c.is_archived).length, [categories]);

    // Sorted & filtered categories
    const displayCategories = useMemo(() => {
        const _categories = showArchivedCategories ? categories : categories.filter(c => !c.is_archived);
        return _categories.sort((a, b) => {
            const aVal = getCatSortValue(a, catSortKey);
            const bVal = getCatSortValue(b, catSortKey);
            if (aVal < bVal) return catSortOrder === OrderDirection.ASC ? -1 : 1;
            if (aVal > bVal) return catSortOrder === OrderDirection.ASC ? 1 : -1;
            return 0;
        });
    }, [categories, catSortKey, catSortOrder, showArchivedCategories]);

    // Sort toggle helper
    const toggleKwSort = (key: SearchRankingsSortKey) => {
        if (kwSortKey === key) {
            setKwSortOrder(prev => (prev === OrderDirection.ASC ? OrderDirection.DESC : OrderDirection.ASC));
        } else {
            setKwSortKey(key);
            setKwSortOrder(OrderDirection.ASC);
        }
    };

    const toggleCatSort = (key: SearchRankingsSortKey) => {
        if (catSortKey === key) {
            setCatSortOrder(prev => (prev === OrderDirection.ASC ? OrderDirection.DESC : OrderDirection.ASC));
        } else {
            setCatSortKey(key);
            setCatSortOrder(OrderDirection.ASC);
        }
    };

    const renderSortIcon = (active: boolean, order: OrderDirection) => (
        <Icon
            icon={active ? (order === OrderDirection.ASC ? 'mdi:arrow-up' : 'mdi:arrow-down') : 'mdi:arrow-up-down'}
            className={`sort-icon${active ? ' active' : ''}`}
        />
    );

    const allKeywordItems = useMemo(() => {
        const items: Array<{id: string; name: string}> = [];
        for (const k of keywords.filter(kw => !kw.is_archived)) {
            items.push({id: k.keyword, name: k.keyword});
        }
        for (const item of kwTrendItems) {
            if (item.dashed && !items.find(i => i.id === item.id)) {
                items.push({id: item.id, name: item.name});
            }
        }
        return items;
    }, [keywords, kwTrendItems]);

    // Top N for keywords: select top N from what's currently available in the selector, skip empty series
    function handleKwResetTopN() {
        const hasData = (id: string) => {
            const plotSeries = kwTrendItems.find(i => i.id === id);
            return plotSeries != null && plotSeries.data.length > 0 && plotSeries.data.some(data => data.value != null);
        };
        const available = allKeywordItems.filter(i => hasData(i.id));
        const ids = available.slice(0, 5).map(i => i.id);
        handleKwTrendSelectionChange(ids);
    }

    const allCategoryItems = useMemo(
        () => categories.filter(c => !c.is_archived).map(c => ({id: c.category_id, name: c.category_name})),
        [categories]
    );

    function renderKeywords() {
        return (
            <BolmateCollapsibleSection
                title='Keyword Performance'
                icon='mdi:layers-outline'
                rightContent={
                    <div className='section-actions'>
                        {archivedKeywordsCount > 0 && (
                            <BolmateButton
                                label={
                                    showArchivedKeywords
                                        ? 'Verberg gearchiveerd'
                                        : `Gearchiveerd (${archivedKeywordsCount})`
                                }
                                onClick={() => setShowArchivedKeywords(!showArchivedKeywords)}
                                size='small'
                                tertiary
                                ornaments={{
                                    element: (
                                        <Icon
                                            icon={showArchivedKeywords ? 'mdi:eye-outline' : 'mdi:eye-off-outline'}
                                            style={{fontSize: '0.875rem'}}
                                        />
                                    )
                                }}
                            />
                        )}
                    </div>
                }
            >
                {loadingKeywords ? (
                    <div className='detail-empty-state'>
                        <Icon icon='mdi:loading' className='spin detail-empty-icon' />
                    </div>
                ) : keywords.length + suggestedKeywords.length === 0 && !loadingSuggestedKeywords ? (
                    <div className='detail-empty-state'>
                        <Icon icon='mdi:magnify' className='detail-empty-icon' />
                        <span className='detail-empty-title'>Geen data</span>
                        <span className='detail-empty-text'>
                            Nog geen keywords voor dit product. Voeg er een toe om rankings te tracken.
                        </span>
                    </div>
                ) : (
                    <>
                        <div className='detail-table-wrapper'>
                            <table className='detail-table'>
                                <thead>
                                    <tr>
                                        <th className='col-label' onClick={() => toggleKwSort('label')}>
                                            <span>Keyword</span>
                                            {renderSortIcon(kwSortKey === 'label', kwSortOrder)}
                                        </th>
                                        <th className='col-metric' onClick={() => toggleKwSort('volume')}>
                                            <span>Zoek Vol.</span>
                                            {renderSortIcon(kwSortKey === 'volume', kwSortOrder)}
                                        </th>
                                        <th className='col-metric' onClick={() => toggleKwSort('winning_bid')}>
                                            <BolmateTooltip text='Winnend bod voor advertenties'>
                                                <span>CPC</span>
                                            </BolmateTooltip>
                                            {renderSortIcon(kwSortKey === 'winning_bid', kwSortOrder)}
                                        </th>
                                        <th className='col-rank' onClick={() => toggleKwSort('week')}>
                                            <span>Week</span>
                                            {renderSortIcon(kwSortKey === 'week', kwSortOrder)}
                                        </th>
                                        <th className='col-rank' onClick={() => toggleKwSort('month')}>
                                            <span>Maand</span>
                                            {renderSortIcon(kwSortKey === 'month', kwSortOrder)}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayKeywords.map(renderKeyword)}
                                    {loadingSuggestedKeywords
                                        ? renderLoadingSuggested()
                                        : displaySuggestedKeywords.map(renderSuggestedKeyword)}
                                    {renderAddKeyword()}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </BolmateCollapsibleSection>
        );
    }

    function renderKeyword(keyword: SearchRankingsKeywordData) {
        return (
            <tr
                key={keyword.keyword}
                className={classNames('detail-row hover-effect', {archived: keyword.is_archived})}
            >
                <td className='col-label'>
                    <div className='label-cell'>
                        <div className='label-cell-left'>
                            <div className='label-icon-box'>
                                <Icon icon='mdi:pound' className='label-icon' />
                            </div>
                            <BolmateTooltip text={keyword.keyword}>
                                <span className='label-text'>{keyword.keyword}</span>
                            </BolmateTooltip>
                            <BolmateTooltip text='Open keyword in Zoekvolume'>
                                <button
                                    className='zoekvolume-btn keyword-action'
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        setNavigationParam([keyword.keyword]);
                                        navigate(SEARCH_VOLUMES_ROUTE);
                                    }}
                                >
                                    <AwesomeIcon name='search' className='zoekvolume-icon' />
                                </button>
                            </BolmateTooltip>
                            <BolmateTooltip text={keyword.source === 'AUTO' ? 'Via Bol API' : 'Handmatig toegevoegd'}>
                                <span className='keyword-source-btn keyword-action'>
                                    <AwesomeIcon
                                        name={keyword.source === 'AUTO' ? 'location-dot' : 'location-arrow'}
                                        className='keyword-source-icon'
                                    />
                                </span>
                            </BolmateTooltip>
                        </div>
                        <div className='label-cell-right'>
                            <BolmateTooltip text={keyword.is_archived ? 'Toon Keyword' : 'Verberg Keyword'}>
                                <button
                                    className={classNames(
                                        'archive-btn keyword-action',
                                        keyword.is_archived && 'archived'
                                    )}
                                    onClick={onArchiveKeyword(keyword.keyword, keyword.is_archived)}
                                >
                                    <Icon
                                        icon={keyword.is_archived ? 'mdi:eye-outline' : 'mdi:eye-off-outline'}
                                        style={{fontSize: '0.875rem'}}
                                    />
                                </button>
                            </BolmateTooltip>
                            <BolmateTooltip text='Bekijk op bol.com'>
                                <a
                                    className='bol-link keyword-action'
                                    href={`https://www.bol.com/nl/nl/s/?searchtext=${encodeURIComponent(
                                        keyword.keyword
                                    )}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                >
                                    <Icon icon='mdi:open-in-new' style={{fontSize: '0.8125rem'}} />
                                </a>
                            </BolmateTooltip>
                        </div>
                    </div>
                </td>
                <td className='col-metric'>
                    {keyword.search_volume !== null ? keyword.search_volume.toLocaleString() : '\u2014'}
                </td>
                <td className='col-metric'>{formatCurrency(keyword.winning_bid, '\u2014')}</td>
                <td className='col-rank'>
                    <RankCell rank={keyword.rank_week} previousRank={keyword.rank_last_week} />
                </td>
                <td className='col-rank'>
                    <RankCell rank={keyword.rank_month} previousRank={keyword.rank_last_month} />
                </td>
            </tr>
        );
    }

    function renderLoadingSuggested() {
        return (
            <tr className='detail-row suggested'>
                <td className='col-label'>
                    <div className='label-cell'>
                        <div className='label-cell-left'>
                            <BolmateTooltip text='Suggesties aan het laden...'>
                                <div className='label-icon-box'>
                                    <Icon icon='mdi:circle-arrows' className='label-icon spin' />
                                </div>
                            </BolmateTooltip>
                            <i className='label-text'>Suggesties aan het ophalen...</i>
                        </div>
                    </div>
                </td>
                <td className='col-metric' colSpan={5} />
            </tr>
        );
    }

    function renderSuggestedKeyword(suggestedKeyword: SuggestedKeyword) {
        if (keywords.length >= MAX_KEYWORDS) return null;
        const {keyword, accepted} = suggestedKeyword;
        const icon = accepted ? 'mdi:pound' : 'mdi:lightbulb-outline';
        return (
            <tr key={keyword} className='detail-row suggested'>
                <td className='col-label'>
                    <div className='label-cell'>
                        <div className='label-cell-left'>
                            <BolmateTooltip text='Suggestie'>
                                <div className='label-icon-box'>
                                    <Icon icon={icon} className='label-icon' />
                                </div>
                            </BolmateTooltip>
                            <BolmateTooltip text={keyword}>
                                <i className='label-text'>{keyword}</i>
                            </BolmateTooltip>
                            <div className={classNames('suggestion-actions keyword-action', {accepted})}>
                                <BolmateTooltip text='Keyword toevoegen'>
                                    <button onClick={onAcceptKeyword(keyword)} className='suggestion-action accept'>
                                        <AwesomeIcon name='check' className='suggestion-icon' />
                                    </button>
                                </BolmateTooltip>
                                <BolmateTooltip text='Suggestie negeren'>
                                    <button className='suggestion-action deny' onClick={onDenyKeyword(keyword)}>
                                        <AwesomeIcon name='times' className='suggestion-icon' />
                                    </button>
                                </BolmateTooltip>
                            </div>
                        </div>
                    </div>
                </td>
                <td className='col-metric' colSpan={5} />
            </tr>
        );
    }

    function renderAddKeyword() {
        const placeholder: string = keywordsLimitReached ? 'Keywords limiet bereikt' : 'Voeg een keyword toe...';
        return (
            <tr className='detail-row add-keyword'>
                <td colSpan={6}>
                    <div className='add-keyword-cell'>
                        <BolmateTooltip text='Keyword toevoegen'>
                            <div className='label-icon-box'>
                                <Icon icon='mdi:plus' className='label-icon' />
                            </div>
                        </BolmateTooltip>
                        <input
                            className='add-keyword-input'
                            onChange={onChangeKeywordValue}
                            onKeyDown={onKeywordValueKeyDown}
                            value={addKeywordValue}
                            placeholder={placeholder}
                            disabled={keywordsLimitReached}
                        />
                        {keywords.length < MAX_KEYWORDS && !keywordsLimitReached && (
                            <BolmateButton
                                label='Laad nieuwe suggesties'
                                onClick={loadMoreSuggestedKeywords}
                                disabled={loadingSuggestedKeywords}
                                size='small'
                                tertiary
                                ornaments={{
                                    element: <Icon icon='mdi:plus' style={{fontSize: '0.75rem'}} />
                                }}
                            />
                        )}
                    </div>
                </td>
            </tr>
        );
    }

    function renderCategories() {
        return (
            <BolmateCollapsibleSection
                title='Categorie Performance'
                icon='mdi:tag-outline'
                rightContent={
                    <div className='section-actions'>
                        {archivedCategoriesCount > 0 && (
                            <BolmateButton
                                label={
                                    showArchivedCategories
                                        ? 'Verberg gearchiveerd'
                                        : `Gearchiveerd (${archivedCategoriesCount})`
                                }
                                onClick={() => setShowArchivedCategories(!showArchivedCategories)}
                                size='small'
                                tertiary
                                ornaments={{
                                    element: (
                                        <Icon
                                            icon={showArchivedCategories ? 'mdi:eye-outline' : 'mdi:eye-off-outline'}
                                            style={{fontSize: '0.875rem'}}
                                        />
                                    )
                                }}
                            />
                        )}
                    </div>
                }
            >
                {loadingCategories ? (
                    <div className='detail-empty-state-wrapper'>
                        <div className='detail-empty-state'>
                            <Icon icon='mdi:loading' className='spin detail-empty-icon' />
                        </div>
                    </div>
                ) : categories.length === 0 ? (
                    <div className='detail-empty-state-wrapper'>
                        <div className='detail-empty-state'>
                            <Icon icon='mdi:tag-outline' className='detail-empty-icon' />
                            <span className='detail-empty-title'>Geen data</span>
                            <span className='detail-empty-text'>Nog geen categorieën gevonden voor dit product.</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className='detail-table-wrapper'>
                            <table className='detail-table'>
                                <thead>
                                    <tr>
                                        <th className='col-label' onClick={() => toggleCatSort('label')}>
                                            <span>Category</span>
                                            {renderSortIcon(catSortKey === 'label', catSortOrder)}
                                        </th>
                                        <th className='col-metric' onClick={() => toggleCatSort('winning_bid')}>
                                            <BolmateTooltip text='Winnend bod voor advertenties'>
                                                <span>CPC</span>
                                            </BolmateTooltip>
                                            {renderSortIcon(catSortKey === 'winning_bid', catSortOrder)}
                                        </th>
                                        <th className='col-rank' onClick={() => toggleCatSort('week')}>
                                            <span>Week</span>
                                            {renderSortIcon(catSortKey === 'week', catSortOrder)}
                                        </th>
                                        <th className='col-rank' onClick={() => toggleCatSort('month')}>
                                            <span>Maand</span>
                                            {renderSortIcon(catSortKey === 'month', catSortOrder)}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>{displayCategories.map(renderCategory)}</tbody>
                            </table>
                        </div>
                    </>
                )}
            </BolmateCollapsibleSection>
        );
    }

    function renderCategory(category: SearchRankingsCategoryData) {
        return (
            <tr
                key={category.category_id}
                className={classNames('detail-row hover-effect', {archived: category.is_archived})}
            >
                <td className='col-label'>
                    <div className='label-cell'>
                        <div className='label-cell-left'>
                            <div className='label-icon-box'>
                                <Icon icon='mdi:tag-outline' className='label-icon' />
                            </div>
                            <BolmateTooltip text={category.category_name}>
                                <span className='label-text'>{category.category_name}</span>
                            </BolmateTooltip>
                        </div>
                        <BolmateTooltip text={category.is_archived ? 'Toon Categorie' : 'Verberg Categorie'}>
                            <button
                                className={classNames('archive-btn keyword-action', category.is_archived && 'archived')}
                                onClick={onArchiveCategory(category.category_id, !category.is_archived)}
                            >
                                <Icon
                                    icon={category.is_archived ? 'mdi:eye-outline' : 'mdi:eye-off-outline'}
                                    style={{fontSize: '0.875rem'}}
                                />
                            </button>
                        </BolmateTooltip>
                    </div>
                </td>
                <td className='col-metric'>{formatCurrency(category.winning_bid, '\u2014')}</td>
                <td className='col-rank'>
                    <RankCell rank={category.rank_week} previousRank={category.rank_last_week} />
                </td>
                <td className='col-rank'>
                    <RankCell rank={category.rank_month} previousRank={category.rank_last_month} />
                </td>
            </tr>
        );
    }

    function renderKeywordTrends() {
        return (
            <BolmateCollapsibleSection title='Keyword Ranking Trend' icon='mdi:chart-line'>
                <BolmatePlot
                    series={kwTrendItems}
                    allItems={allKeywordItems}
                    activeIds={kwTrendSelectedIds}
                    onSelectionChange={handleKwTrendSelectionChange}
                    onResetTopN={handleKwResetTopN}
                    reversed
                    yAxisMin={1}
                    selectorLabel='Keywords'
                    showTopN
                    height={250}
                    loading={kwTrendLoading}
                    emptyIcon='mdi:chart-line'
                    emptyTitle='Geen data'
                    emptyMessage='Selecteer een serie of pas de filters aan om gegevens te bekijken.'
                />
            </BolmateCollapsibleSection>
        );
    }

    function renderCategoryTrends() {
        return (
            <BolmateCollapsibleSection title='Categorie Ranking Trend' icon='mdi:chart-timeline-variant'>
                <BolmatePlot
                    series={catTrendItems}
                    allItems={allCategoryItems}
                    activeIds={catTrendSelectedIds}
                    onSelectionChange={handleCatTrendSelectionChange}
                    reversed
                    yAxisMin={1}
                    selectorLabel='Categorieën'
                    height={250}
                    loading={catTrendLoading}
                    emptyIcon='mdi:chart-timeline-variant'
                    emptyTitle='Geen data'
                    emptyMessage='Geen categorie trenddata beschikbaar.'
                />
            </BolmateCollapsibleSection>
        );
    }

    function renderBuyBoxTrends() {
        return (
            <BolmateCollapsibleSection title='Koopblok Dominantie' icon='mdi:shield-check-outline'>
                <BolmatePlot
                    series={buyboxTrendData}
                    allItems={[{id: 'buybox', name: 'Koopblok %'}]}
                    activeIds={['buybox']}
                    onSelectionChange={() => {}}
                    yAxisMin={0}
                    yAxisMax={100}
                    yAxisSuffix='%'
                    selectorLabel='Koopblok'
                    height={250}
                    loading={buyboxLoading}
                    hideSelector
                    emptyIcon='mdi:shield-check-outline'
                    emptyTitle='Geen data'
                    emptyMessage='Geen koopblok data beschikbaar voor dit product.'
                />
            </BolmateCollapsibleSection>
        );
    }

    return (
        <div className='product-detail-panel' onClick={stopPropagation}>
            {/* Section 1: Keyword Performance */}
            {renderKeywords()}

            {/* Section 2: Category Performance */}
            {renderCategories()}

            {/* Section 3: Keyword Ranking History */}
            {renderKeywordTrends()}

            {/* Section 4: Category Ranking History */}
            {renderCategoryTrends()}

            {/* Section 5: Buybox Dominance */}
            {renderBuyBoxTrends()}

            {/* Close button */}
            <div className='detail-panel-footer'>
                <button className='close-details-btn' onClick={onClose}>
                    <Icon icon='mdi:chevron-up' className='close-details-icon' />
                    <span>Sluit Product Details</span>
                </button>
            </div>
        </div>
    );
}
